import type { SubscriptionType } from "gql-types/schemas";
import { SubscriptionStatus } from "gql-types/schemas";

import { getIsPro, getIsTeamAdmin } from "utils/subscription";

import { useSession } from "./api/useSession";
import { useActiveWorkspace } from "./useActiveWorkspace";
import { useWorkspaceFeatureFlags } from "./useWorkspaceFeatureFlags";

/**
 * Trying to keep these as explicit as possible
 * undefined means we cannot tell yet if the team is there or not
 * so we cannot tell if the use is Pro or Free yet
 * This forces us to use isPro / isFree to conditionally
 * add features/blockers at runtime
 */
export type WorkspaceSubscriptionReturn =
  | {
      id: undefined;
      subscription: undefined;
      isPro: undefined;
      isFree: undefined;
      isTeamAdmin: undefined;
      isCodeSandboxTeam: undefined;
      hasPendingPayment: undefined;
      hasReachedPrivateSandboxLimit: undefined;
      privateSandboxesLimit?: undefined;
    }
  | {
      id: null;
      subscription: null;
      isPro: false;
      isFree: true;
      isTeamAdmin: boolean;
      isCodeSandboxTeam: false;
      hasPendingPayment: false;
      hasReachedPrivateSandboxLimit: boolean;
      privateSandboxesLimit?: number;
    }
  | {
      id: string;
      subscription: {
        cancelAt?: string;
        status: SubscriptionStatus;
        type: SubscriptionType;
        trialEnd?: string;
        trialStart?: string;
      };
      isTeamAdmin: boolean;
      isPro: boolean;
      isFree: boolean;
      isCodeSandboxTeam: boolean;
      hasPendingPayment: boolean;
      hasReachedPrivateSandboxLimit: boolean;
      privateSandboxesLimit?: number;
    };

export const useActiveWorkspaceSubscription =
  (): WorkspaceSubscriptionReturn => {
    const activeWorkspace = useActiveWorkspace();
    const [session] = useSession();
    const featureFlags = useWorkspaceFeatureFlags();

    if (activeWorkspace === undefined) {
      return {
        id: undefined,
        subscription: undefined,
        isPro: undefined,
        isFree: undefined,
        isTeamAdmin: undefined,
        isCodeSandboxTeam: undefined,
        hasPendingPayment: undefined,
        hasReachedPrivateSandboxLimit: undefined,
        privateSandboxesLimit: undefined,
      };
    }

    const { subscription, limits, usage } = activeWorkspace;
    const currentUserId =
      session.state === "AUTHENTICATED" ? session.user.id : null;

    const isTeamAdmin = currentUserId
      ? getIsTeamAdmin(currentUserId, activeWorkspace)
      : false;

    const shouldApplySandboxLimits =
      featureFlags.ubbBeta && !featureFlags.friendOfCsb;

    const hasReachedPrivateSandboxLimit =
      shouldApplySandboxLimits &&
      usage.privateSandboxesQuantity >= limits.includedPrivateSandboxes;

    /**
     * If subscription is null or undefined, but the team exists
     * It means the team never had a pro account, so we default to explicit values
     */
    if (subscription === null || subscription === undefined) {
      return {
        id: activeWorkspace.id,
        subscription: null,
        isPro: false,
        isTeamAdmin,
        isFree: true,
        isCodeSandboxTeam: false,
        hasPendingPayment: false,
        hasReachedPrivateSandboxLimit,
        privateSandboxesLimit: limits.includedPrivateSandboxes,
      };
    }

    const isPro = getIsPro(activeWorkspace);
    const isFree = !isPro;

    const isCodeSandboxTeam = CODESANDBOX_TEAM_IDS.includes(activeWorkspace.id);

    const hasPendingPayment =
      subscription.status === SubscriptionStatus.Unpaid ||
      subscription.status === SubscriptionStatus.Incomplete;

    return {
      id: activeWorkspace.id,
      subscription,
      isPro,
      isTeamAdmin,
      isFree,
      isCodeSandboxTeam,
      hasPendingPayment,
      hasReachedPrivateSandboxLimit,
      privateSandboxesLimit: limits.includedPrivateSandboxes,
    };
  };

const CODESANDBOX_TEAM_IDS = [
  "b5123bcc-da66-446f-88e2-5b89c9c37d27", // stream
  "642af90c-4717-4730-bad3-e4c1e37ca5e2", // prd
];
